/**
 * Student Invoice Service
 * Handles fetching invoices, details, PDF URLs, and recalculating installments for the student portal.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { PORTAL_FINANCE_ENDPOINTS, buildUrlWithParams } from '../api';
import {
  StudentInvoice,
  StudentInvoiceDetailResponse,
  StudentInvoicePosition,
  InvoiceSearchResponse,
  StudentInstallmentStatus,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class StudentInvoiceService {
  private readonly http = inject(HttpClient);

  /**
   * Get all invoices for the student contact
   * Ordered chronologically (oldest to newest)
   */
  getInvoices(contactId: number): Observable<StudentInvoice[]> {
    const url = buildUrlWithParams(PORTAL_FINANCE_ENDPOINTS.invoicesSearch, {
      fk_contact_id: contactId,
      limit: 1000,
      offset: 0,
    });

    return this.http.get<InvoiceSearchResponse>(url).pipe(
      map((response) => {
        const invoices = response.results || [];
        // Map and ensure gross/open amounts are floats
        return invoices.map((inv) => {
          const gross = Number(inv.amount_gross || 0);
          let open = inv.open_amount !== undefined && inv.open_amount !== null
            ? Number(inv.open_amount)
            : gross;

          // If status is paid (652), open_amount is 0
          if (inv.fk_status_id === 652) {
            open = 0;
          }

          return {
            ...inv,
            amount_gross: gross,
            amount_net: Number(inv.amount_net || 0),
            amount_vat: Number(inv.amount_vat || 0),
            open_amount: open,
            paid_amount: Math.max(0, gross - open),
          };
        });
      })
    );
  }

  /**
   * Get full invoice details including positions and installments
   */
  getInvoiceDetails(invoiceId: number): Observable<StudentInvoiceDetailResponse> {
    const url = PORTAL_FINANCE_ENDPOINTS.invoiceDetails(invoiceId);
    return this.http.get<any>(url).pipe(
      map((raw) => {
        // Flatten installments if nested
        const rawInsts = raw.installments || [];
        const installments = rawInsts.map((item: any) => {
          const inst = item.installment || item;
          const payments = item.payments || [];
          return {
            ...inst,
            amount: Number(inst.amount || 0),
            open_amount: Number(inst.open_amount !== undefined ? inst.open_amount : inst.amount || 0),
            payments,
          };
        });

        const rawPositions = raw.positions || [];
        const positions: StudentInvoicePosition[] = rawPositions
          .map((pos: any) => ({
            ...pos,
            position: pos.position !== null && pos.position !== undefined ? Number(pos.position) : 1,
            amount_net: Number(pos.amount_net || 0),
            amount_vat: Number(pos.amount_vat || 0),
            amount_gross: Number(pos.amount_gross || 0),
            quantity: pos.quantity !== null && pos.quantity !== undefined ? Number(pos.quantity) : 1,
          }))
          .sort((a: StudentInvoicePosition, b: StudentInvoicePosition) => (a.position || 0) - (b.position || 0));

        return {
          invoice_id: raw.invoice_id,
          fk_status_id: raw.fk_status_id,
          invoice_status: raw.invoice_status,
          invoice_date: raw.invoice_date,
          due_date: raw.due_date,
          degree_code: raw.degree_code,
          module_code: raw.module_code,
          positions,
          installments,
        };
      })
    );
  }

  /**
   * Get secure PDF download SAS URL
   */
  getInvoicePdfUrl(invoiceId: number): Observable<{ invoice_id: number; pdf_url: string }> {
    const url = PORTAL_FINANCE_ENDPOINTS.invoicePdfUrl(invoiceId);
    return this.http.get<{ invoice_id: number; pdf_url: string }>(url);
  }

  /**
   * Download invoice PDF directly from backend as a Blob
   * Completely masks Azure Blob Storage URLs, avoiding CORS restrictions and URL exposure.
   */
  downloadInvoicePdfBlob(invoiceId: number): Observable<Blob> {
    const url = PORTAL_FINANCE_ENDPOINTS.invoicePdfStream(invoiceId);
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Get installment status and school surcharge info for the student
   */
  getStudentInstallmentStatus(contactId: number): Observable<StudentInstallmentStatus[]> {
    const url = PORTAL_FINANCE_ENDPOINTS.studentInstallmentStatus(contactId);
    return this.http.get<StudentInstallmentStatus[]>(url);
  }

  /**
   * Recalculate installments for Planned invoices when switching payment mode
   */
  recalculateInstallments(
    contactId: number,
    enableInstallments: boolean,
    schoolId?: number | null
  ): Observable<{ success: boolean; modified_invoices: number; installments_count: number; has_installments_enabled: boolean }> {
    const url = PORTAL_FINANCE_ENDPOINTS.recalculateInstallments;
    return this.http.post<{ success: boolean; modified_invoices: number; installments_count: number; has_installments_enabled: boolean }>(url, {
      contact_id: contactId,
      school_id: schoolId ?? null,
      enable_installments: enableInstallments,
    });
  }
}

